import type { FC } from "react"

import { ADDER_CATEGORY } from "@logic/lib/powur-os"
import { useTranslation } from "react-i18next"
import { useRecoilState, useRecoilValue } from "recoil"
import styled from "styled-components"

import { GroundMount } from "~/components/Icons"
import {
  ProjectService,
  Electrical,
  Generators,
  Mounting,
  Travel,
  Battery,
  Misc,
  Roofing,
  AllAdders,
  Engineering,
} from "~/components/Icons/adderIcons"
import { fontWeight } from "~/utils/styles"

import {
  categoriesWithAddersState,
  selectedCategoryState,
} from "../../../../state/adders"

const Container = styled.div`
  flex-shrink: 0;
  max-width: 250px;
  margin: 20px 0 0;
  padding: 10px 25px 10px 15px;
  background-color: #f8f8f8;
  height: 100%;
  overflow-y: auto;
  overflow-x: visible;
  ul {
    list-style: none;
    li {
      display: flex;
      align-items: center;
      flex-grow: 1;
      width: 100%;
      height: 36px;
      padding: 0 10px;
      font-size: 13px;
      line-height: 16px;
      border: 1px solid transparent;
      border-radius: 4px;
      margin-bottom: 6px;
      ${fontWeight(500)}
      svg {
        height: 20px;
        width: 20px;
        color: var(--blue);
        margin-right: 10px;
      }
      :hover,
      &.selected {
        transition: all 0.3s ease;
        cursor: pointer;
      }
      &.selected {
        background-color: var(--blue);
        color: var(--white);
        transform: translate(10px, 0);
        svg {
          color: var(--white);
        }
      }
      :hover {
        border: 1px solid var(--blue);
      }
    }
  }
`

type AdderCategoryDisplay = {
  id: ADDER_CATEGORY
  name:
    | `allAdders`
    | `batteryInstallation`
    | `cancellationFees`
    | `electrical`
    | `engineering`
    | `generators`
    | `groundMount`
    | `leadAcquisition`
    | `miscellaneous`
    | `mounting`
    | `projectService`
    | `rebates`
    | `roofing`
    | `travel`
    | `warranties`
  icon: JSX.Element
}

const CATEGORIES: Readonly<AdderCategoryDisplay[]> = [
  {
    id: ADDER_CATEGORY[`All Adders`],
    name: `allAdders`,
    icon: <AllAdders />,
  },
  {
    id: ADDER_CATEGORY[`Battery Installation`],
    name: `batteryInstallation`,
    icon: <Battery />,
  },
  {
    id: ADDER_CATEGORY[`Electrical`],
    name: `electrical`,
    icon: <Electrical />,
  },
  {
    id: ADDER_CATEGORY[`Generators`],
    name: `generators`,
    icon: <Generators />,
  },
  {
    id: ADDER_CATEGORY[`Ground Mounts`],
    name: `groundMount`,
    icon: <GroundMount />,
  },
  {
    id: ADDER_CATEGORY[`Miscellaneous`],
    name: `miscellaneous`,
    icon: <Misc />,
  },
  {
    id: ADDER_CATEGORY[`Mounting`],
    name: `mounting`,
    icon: <Mounting />,
  },
  {
    id: ADDER_CATEGORY[`Base Services`],
    name: `projectService`,
    icon: <ProjectService />,
  },
  {
    id: ADDER_CATEGORY[`Roof Type`],
    name: `roofing`,
    icon: <Roofing />,
  },
  {
    id: ADDER_CATEGORY[`Travel`],
    name: `travel`,
    icon: <Travel />,
  },
  {
    id: ADDER_CATEGORY[`Rebates`],
    name: `rebates`,
    icon: <ProjectService />,
  },
  {
    id: ADDER_CATEGORY[`Warranties`],
    name: `warranties`,
    icon: <Engineering />,
  },
  {
    id: ADDER_CATEGORY[`Lead Acquisition`],
    name: `leadAcquisition`,
    icon: <Misc />,
  },
]

export const Category: FC = () => {
  const { t } = useTranslation()

  const [selectedCategory, setSelectedCategory] = useRecoilState(
    selectedCategoryState,
  )

  const categories = useRecoilValue(categoriesWithAddersState)

  return (
    <Container>
      <ul>
        {CATEGORIES.filter(({ id }) => categories.includes(id)).map(
          (category) => {
            const { id, name, icon } = category
            const isSelected = id === selectedCategory

            return (
              <li
                key={id}
                className={isSelected ? `selected` : ``}
                onClick={() => {
                  setSelectedCategory(id)
                }}
              >
                {icon}
                {t(name)}
              </li>
            )
          },
        )}
      </ul>
    </Container>
  )
}
